import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  tableCellClasses,
} from '@mui/material';
import { styled as muiStyled, useTheme } from '@mui/material/styles';
import React from 'react';

import ConnectAssistant from './connect-assistant';
import { ActivityChip, AttestationChip } from './monitoring-chip';
import { mapControlSurfaceToStatus } from './monitoring-control-surface-adapter';
import MonitoringInheritedPolicies from './monitoring-inherited-policies';
import MonitoringPublicationHistory from './monitoring-publication-history';
import MonitoringPublishStatus from './monitoring-publish-status';
import { computeAttestationGap } from './monitoring-status-provider';
import type { MonitoringAttestation } from './monitoring-types';
import ModalComponent from '../../../project-table/archive-project-modal';

import Button from '~/client/components/base/button';
import { getDesignTokens } from '~/client/components/base/core';
import { ArrowRight } from '~/client/components/base/icon';
import { CircularProgress } from '~/client/components/base/progress';
import SearchAndFiltersBar from '~/client/components/base/search-and-filters-bar';
import { TableAction } from '~/client/components/base/table';
import Text from '~/client/components/base/text';
import PolicyItem from '~/client/components/policy/policy-item';
import { useDashboardDataContext } from '~/client/components/project/dashboard/dashboard-context';
import * as GQL from '~/graphql/generated/client';

const StyledTableCell = muiStyled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: '14px',
    fontWeight: 600,
    padding: '16px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '16px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
}));

const StyledTableRow = muiStyled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  cursor: 'pointer',
}));

type ActivityFilter = '' | 'active' | 'idle';
type AttestationFilter = '' | 'attested' | 'unverified';

interface Props {
  assessments: GQL.AssessmentFragment[];
  canManageAssessments: boolean;
  removeAssessmentHandler: (id: string) => void;
  removeAssessmentLoading: boolean;
  onRowClick: (assessmentId: string) => void;
  className?: string;
}

// ── MonitoringPolicyRow ──────────────────────────────────────────────────────
//
// One row = one live guardianControlSurface query. Extracted into its own
// component because each row needs its own hook call (React's rules of
// hooks forbid calling a query hook once per assessment inside a .map()) —
// there is no batched "all policies" query; the resolver is single-policy
// by design (PHASE-0-BUILD-SPEC.md §G1b).
//
// Activity/attestation filtering therefore happens PER ROW against its own
// live data rather than pre-filtering a synchronous list: the row always
// mounts (so its query always fires), computes whether it matches the
// current filters once its data resolves, and reports that back up via
// `onMatchResolved` so the table can show an accurate "no policies match"
// message without ever hiding a row before its query has actually settled
// (a row still loading always renders — never counted as a non-match).

interface MonitoringPolicyRowProps {
  assessment: GQL.AssessmentFragment;
  policyId: string;
  policyName: string;
  projectId: string;
  activityFilter: ActivityFilter;
  attestationFilter: AttestationFilter;
  canManageAssessments: boolean;
  removeAssessmentHandler: (id: string) => void;
  removeAssessmentLoading: boolean;
  onRowClick: (assessmentId: string) => void;
  onMatchResolved: (assessmentId: string, matches: boolean) => void;
  className?: string;
}

const MonitoringPolicyRow: React.FC<MonitoringPolicyRowProps> = ({
  assessment,
  policyId,
  policyName,
  projectId,
  activityFilter,
  attestationFilter,
  canManageAssessments,
  removeAssessmentHandler,
  removeAssessmentLoading,
  onRowClick,
  onMatchResolved,
  className,
}) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  const assessmentId = assessment._id;

  const { data, loading, error } = GQL.useGetGuardianControlSurfaceQuery({
    variables: { projectId, policyId, assessmentId },
    skip: !projectId || !policyId || !assessmentId,
    fetchPolicy: 'cache-and-network',
  });

  React.useEffect(() => {
    if (error && process.env.NODE_ENV !== 'production') {
      console.warn(
        `[monitoring] guardianControlSurface query failed for policyId="${policyId}": ${error.message}`
      );
    }
  }, [error, policyId]);

  // True only while the FIRST fetch for this row is in flight — the
  // background half of `cache-and-network` never re-triggers this.
  const stillLoading = loading && !data;

  const snapshot = React.useMemo(
    () =>
      data?.guardianControlSurface
        ? mapControlSurfaceToStatus(data.guardianControlSurface)
        : null,
    [data]
  );

  const hasSummary = Boolean(snapshot);
  const activeCount = snapshot
    ? snapshot.controls.filter((c) => c.activity === 'active').length
    : 0;
  const total = snapshot?.summary.totalControls ?? 0;
  const totalConsults = snapshot?.summary.totalConsults ?? 0;
  const attestationGap = snapshot
    ? computeAttestationGap(snapshot.controls)
    : 0;
  const everExercised = activeCount > 0 || totalConsults > 0;

  const matchesActivity =
    activityFilter === '' ||
    (activityFilter === 'active' && activeCount > 0) ||
    (activityFilter === 'idle' && hasSummary && activeCount === 0);

  const matchesAttestation =
    attestationFilter === '' ||
    (attestationFilter === 'attested' &&
      hasSummary &&
      attestationGap === 0 &&
      everExercised) ||
    (attestationFilter === 'unverified' && attestationGap > 0);

  const matches = matchesActivity && matchesAttestation;

  React.useEffect(() => {
    // Only report once this row has actually settled — a still-loading row
    // must never be counted as a confirmed non-match.
    if (!stillLoading) {
      onMatchResolved(assessment._id, matches);
    }
  }, [stillLoading, matches, assessment._id, onMatchResolved]);

  if (!stillLoading && !matches) {
    return null;
  }

  const activeAttested = activeCount - attestationGap;
  const activeGap = attestationGap;
  const idleCount = total - activeCount;
  const hasSegments = hasSummary && total > 0;

  // Active controls whose attestation is genuinely "not yet verified" — in
  // Phase 0 (H1 chain verification unbuilt) that is EVERY active control.
  // attestationGap is 0 here not because anything passed verification but
  // because nothing has been verified at all, so the rollup must read
  // 'pending', never a fabricated 'attested' green.
  const activePending = snapshot
    ? snapshot.controls.filter(
        (c) => c.activity === 'active' && c.attestation === 'pending'
      ).length
    : 0;

  const rollupAttestation: MonitoringAttestation =
    !hasSummary || !everExercised
      ? 'not_applicable'
      : attestationGap > 0
        ? 'unverified'
        : activePending > 0
          ? 'pending'
          : 'attested';

  return (
    <StyledTableRow onClick={() => onRowClick(assessment._id)}>
      <StyledTableCell>
        {assessment.policy ? (
          <PolicyItem policy={assessment.policy} governanceTab="monitoring" />
        ) : (
          <Text sz="body14">{policyName}</Text>
        )}
      </StyledTableCell>

      <StyledTableCell>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            minWidth: 100,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              height: 5,
              borderRadius: '3px',
              overflow: 'hidden',
            }}
          >
            {hasSegments ? (
              <>
                {activeAttested > 0 && (
                  <Box
                    sx={{
                      flex: activeAttested,
                      backgroundColor: t.status.success,
                    }}
                  />
                )}
                {activeGap > 0 && (
                  <Box
                    sx={{
                      flex: activeGap,
                      backgroundColor: t.status.warning,
                    }}
                  />
                )}
                {idleCount > 0 && (
                  <Box
                    sx={{
                      flex: idleCount,
                      backgroundColor: t.status.neutral,
                    }}
                  />
                )}
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: t.status.neutral,
                  opacity: 0.3,
                }}
              />
            )}
          </Box>
          <Text sz="body12" sx={{ color: t.textMuted }}>
            {stillLoading ? (
              <>
                <CircularProgress
                  size={10}
                  sx={{ verticalAlign: 'middle', mr: 0.5 }}
                />
                Loading…
              </>
            ) : hasSummary ? (
              `${activeCount}/${total} active`
            ) : (
              'Signal unavailable'
            )}
          </Text>
        </Box>
      </StyledTableCell>

      <StyledTableCell>
        {rollupAttestation === 'not_applicable' ? (
          <Text sz="body12" sx={{ color: t.textMuted }}>
            —
          </Text>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AttestationChip value={rollupAttestation} />
            {attestationGap > 0 && (
              <Text sz="body12" sx={{ color: t.status.warning }}>
                ({attestationGap})
              </Text>
            )}
          </Box>
        )}
      </StyledTableCell>

      <StyledTableCell>
        <Text sz="body14" sx={{ color: t.textPrimary }}>
          {hasSummary ? totalConsults : '—'}
        </Text>
      </StyledTableCell>

      {canManageAssessments && (
        <StyledTableCell align="center">
          <ModalComponent
            {...{
              className,
              archiveProjectHandler: removeAssessmentHandler,
              entry: {
                id: assessment._id,
                projectName: policyName,
              },
              archiveProjectLoading: removeAssessmentLoading,
              confirmText: `Are you sure you want to remove monitoring policy '${policyName}'?`,
            }}
          />
        </StyledTableCell>
      )}

      <StyledTableCell>
        <TableAction tertiary>
          <Button
            icon={ArrowRight}
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(assessment._id);
            }}
          />
        </TableAction>
      </StyledTableCell>
    </StyledTableRow>
  );
};

// ── MonitoringTab ─────────────────────────────────────────────────────────────

const MonitoringTab: React.FC<Props> = ({
  assessments,
  canManageAssessments,
  removeAssessmentHandler,
  removeAssessmentLoading,
  onRowClick,
  className,
}) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  const { project } = useDashboardDataContext();
  const projectId = project?._id;

  const [searchInput, setSearchInput] = React.useState('');
  const [activityFilter, setActivityFilter] =
    React.useState<ActivityFilter>('');
  const [attestationFilter, setAttestationFilter] =
    React.useState<AttestationFilter>('');

  // Identity-only entries (policy id/name) — synchronous, no network call.
  // Each row fetches + validates its own live summary independently below.
  const baseEntries = React.useMemo(
    () =>
      assessments
        .filter((assessment) => Boolean(assessment.policy))
        .map((assessment) => ({
          assessment,
          policyId: assessment.policy.identifier,
          policyName: assessment.policy.name,
        })),
    [assessments]
  );

  const searchFilteredEntries = React.useMemo(() => {
    if (!searchInput) {
      return baseEntries;
    }
    const lower = searchInput.toLowerCase();
    return baseEntries.filter((e) =>
      e.policyName.toLowerCase().includes(lower)
    );
  }, [baseEntries, searchInput]);

  // Assessment ids whose row has settled (query resolved or errored) AND
  // does not match the current activity/attestation filters. Anything NOT
  // in this set is treated as visible — either it matches, or it hasn't
  // resolved yet and is still rendering its own loading state.
  const [nonMatching, setNonMatching] = React.useState<Set<string>>(
    () => new Set()
  );

  const handleMatchResolved = React.useCallback(
    (assessmentId: string, matches: boolean) => {
      setNonMatching((prev) => {
        const wasMarked = prev.has(assessmentId);
        if (wasMarked === !matches) {
          return prev;
        }
        const next = new Set(prev);
        if (matches) {
          next.delete(assessmentId);
        } else {
          next.add(assessmentId);
        }
        return next;
      });
    },
    []
  );

  const visibleCount = searchFilteredEntries.filter(
    (e) => !nonMatching.has(e.assessment._id)
  ).length;

  if (baseEntries.length === 0) {
    return (
      <Box sx={{ pt: 2 }}>
        <Text sz="body14" color="secondary">
          No monitoring controls attached to this project yet.
        </Text>
        {projectId && <MonitoringInheritedPolicies projectId={projectId} />}
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2 }}>
      {projectId && (
        <Box sx={{ mb: 2 }}>
          <ConnectAssistant
            projectId={projectId}
            projectName={project?.name ?? 'this project'}
          />
        </Box>
      )}
      {projectId && <MonitoringPublishStatus projectId={projectId} />}
      {projectId && <MonitoringPublicationHistory projectId={projectId} />}
      <SearchAndFiltersBar
        placeholder="Search monitoring policies..."
        value={searchInput}
        onChange={setSearchInput}
      >
        <FormControl size="small" className="filter-field">
          <InputLabel>Activity</InputLabel>
          <Select
            value={activityFilter}
            label="Activity"
            onChange={(e) =>
              setActivityFilter(e.target.value as ActivityFilter)
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="idle">Idle</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" className="filter-field">
          <InputLabel>Attestation</InputLabel>
          <Select
            value={attestationFilter}
            label="Attestation"
            onChange={(e) =>
              setAttestationFilter(e.target.value as AttestationFilter)
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="attested">Attested</MenuItem>
            <MenuItem value="unverified">Attestation gap</MenuItem>
          </Select>
        </FormControl>
      </SearchAndFiltersBar>

      <Box sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell align="left">NAME</StyledTableCell>
              <StyledTableCell align="left">ACTIVITY</StyledTableCell>
              <StyledTableCell align="left">ATTESTATION</StyledTableCell>
              <StyledTableCell align="left">CONSULTS</StyledTableCell>
              {canManageAssessments && (
                <StyledTableCell align="center">ACTIONS</StyledTableCell>
              )}
              <StyledTableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {searchFilteredEntries.map((entry) => (
              <MonitoringPolicyRow
                key={entry.assessment._id}
                assessment={entry.assessment}
                policyId={entry.policyId}
                policyName={entry.policyName}
                projectId={projectId ?? ''}
                activityFilter={activityFilter}
                attestationFilter={attestationFilter}
                canManageAssessments={canManageAssessments}
                removeAssessmentHandler={removeAssessmentHandler}
                removeAssessmentLoading={removeAssessmentLoading}
                onRowClick={onRowClick}
                onMatchResolved={handleMatchResolved}
                className={className}
              />
            ))}
          </TableBody>
        </Table>
        {visibleCount === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Text sz="body14" sx={{ color: t.textMuted }}>
              No policies match your filters.
            </Text>
          </Box>
        )}
      </Box>
      {projectId && <MonitoringInheritedPolicies projectId={projectId} />}
    </Box>
  );
};

export default MonitoringTab;
